const HomeClimateGuide = () => {
  return (
    <section className="py-16 md:py-20 px-4 bg-background">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4 uppercase tracking-wide">
          Heating &amp; Cooling for a Genuine Cool-Climate Region
        </h2>
        <p className="text-muted-foreground text-lg mb-6">
          At 600–750 metres above sea level, the Southern Highlands is nothing like the coastal
          suburbs most air conditioning brochures are written for. Mittagong, Bowral, Moss Vale,
          Berrima, Bundanoon and Robertson all experience regular winter frosts, sub-zero mornings
          and cold nights that can sit below freezing from May through to September. Goulburn and
          the higher parts of Wollondilly are often colder again. If a system isn't specified for
          this climate, homeowners end up with a unit that struggles precisely when it's needed most.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Why heating load matters more than cooling load here</h3>
        <p className="text-muted-foreground mb-4">
          Most of Australia's air conditioning industry sizes systems around summer cooling demand.
          In the Highlands, that's backwards. Our summers are mild and short — the real challenge is
          keeping a home warm and comfortable through long, cold winters. We size every system on its
          winter heating load first, factoring in ceiling height, window area, insulation levels and
          how exposed the property is to wind and frost pockets. A unit that's "big enough" for summer
          in Sydney can be seriously undersized for a Bowral winter.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Frost, defrost cycles and real-world efficiency</h3>
        <p className="text-muted-foreground mb-4">
          Reverse cycle systems draw heat from outside air, but as outdoor temperatures fall towards
          and below zero, the outdoor coil can ice up. The unit then runs a defrost cycle, briefly
          pausing heating to clear the frost. Lower-quality or undersized systems defrost more often
          and for longer, leaving rooms cold while they cycle. We specify systems with genuine
          low-ambient heating performance and good defrost management so they keep delivering warmth
          on the coldest Southern Highlands mornings, not just on a mild Sydney basin day.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Coefficient of performance (COP) in sub-zero starts</h3>
        <p className="text-muted-foreground mb-4">
          A system's COP tells you how much heat it produces for every unit of electricity it
          consumes — but manufacturer COP figures are usually tested at mild outdoor temperatures.
          What matters in Robertson or Bundanoon in July is how that COP holds up when it's -3°C
          outside at 6am. We favour brands and models with published low-ambient heating data and
          verified cold-climate performance, so the running cost figures you're quoted actually
          reflect what you'll see on your power bill through winter, not an optimistic summer number.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Older and heritage homes with poor insulation</h3>
        <p className="text-muted-foreground mb-4">
          Many Highlands properties — from Berrima sandstone cottages to Bowral and Moss Vale
          weatherboard homes — were built long before modern insulation standards, with single-glazed
          windows, uninsulated wall cavities and draughty floors. These homes lose heat quickly, which
          changes the whole equation: oversizing to compensate for poor insulation wastes energy and
          shortens equipment life, while undersizing leaves rooms permanently cold. During our on-site
          assessment we look at wall and ceiling insulation, glazing, and draught sources, and we're
          upfront if simple sealing or insulation upgrades will get you a better outcome than a bigger
          air conditioner.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Running costs through a Highlands winter</h3>
        <p className="text-muted-foreground mb-4">
          Because Highlands winters demand more heating hours per day than most of NSW, running costs
          are a bigger part of the decision than they are in warmer regions. A correctly sized, high
          COP reverse cycle system will typically cost meaningfully less to run through winter than an
          undersized unit fighting to keep up, or an older gas or electric strip heater. We provide
          honest running-cost guidance as part of every quote so you can compare options like for like.
        </p>

        <h3 className="text-xl font-bold text-foreground mt-8 mb-3">Zoning for big country blocks and rambling floor plans</h3>
        <p className="text-muted-foreground mb-4">
          Many Southern Highlands homes sit on larger blocks with rambling, single-level floor plans,
          separate wings, or converted verandahs and sunrooms — very different from a compact suburban
          layout. A single split system rarely heats a whole home like this efficiently. Ducted reverse
          cycle systems with proper zoning let you heat the living areas and main bedroom in the early
          morning and evening, and shut down unused wings, rather than heating the whole house
          uniformly. It's a smarter match for the way big country homes are actually lived in, and it
          keeps running costs under control.
        </p>

        <p className="text-muted-foreground">
          Every recommendation we make starts with your address, your home's construction and how
          cold it actually gets on your block — not a generic capacity chart. Explore our full range
          of <a href="/services" className="text-primary hover:underline font-semibold">heating and cooling services</a>{" "}
          or see how we service your specific town on our{" "}
          <a href="/areas-we-service" className="text-primary hover:underline font-semibold">areas we service</a> page.
        </p>
      </div>
    </section>
  );
};

export default HomeClimateGuide;
